'use client';

import { Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { SeedingContentChannel } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { getPaths } from '../../common';
import { useDdayCountdown, useTrackImpression } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { isMusinsaService } from '../utils/campaignProposalMusinsaAnalytics';
import {
  buildSeedingCampaignImpressionGtmAttr,
  trackSeedingCampaignClickAmplitude,
  trackSeedingCampaignClickMusinsa,
} from '../utils/seedingMusinsaAnalytics';
import { SnsChannelIcon } from './SnsChannelIcon';

interface Props {
  campaignId: number;
  /** 리스트 내 0-based map index (이벤트 노출/클릭 순서용) */
  index: number;
  /** 리스트 item.brandId — 이벤트 brand_id 슬롯 */
  brandId: string;
  goodsImage: string;
  brandName: string;
  goodsName: string;
  goodsPrice: number;
  recruitmentEndAt: string;
  snsChannels: SeedingContentChannel[];
  /** 멀티 슬롯 캠페인의 부가 상품 수 (= goodsSlots.length - 1) */
  extraGoodsCount?: number;
  className?: string;
}

export const CampaignRecruitmentCard = ({
  campaignId,
  index,
  brandId,
  goodsImage,
  brandName,
  goodsName,
  goodsPrice,
  recruitmentEndAt,
  snsChannels,
  extraGoodsCount = 0,
  className,
}: Props) => {
  const { service } = useDeviceInfo();
  const paths = getPaths(service);
  // curatorId — ProposalList 선례: react-query dedupe 로 중복 호출 없음.
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorId = userInfo?.snsTitle ?? '';
  // 상품 썸네일 비율 — 29CM=square(5:5) / 무신사=portrait(세로). 전 지면 공통 정책. (SID-4424)
  const is29cm = service === SERVICE['29CM'];
  const { label, isExpired } = useDdayCountdown(recruitmentEndAt);
  const ddayLabel = isExpired ? '모집 종료' : label;
  const displayGoodsName = extraGoodsCount > 0 ? `${goodsName} 외 ${extraGoodsCount}개` : goodsName;

  // 시딩 캠페인 리스트 > 캠페인 노출 (IMPLEMENTATION.md ① #5)
  // 무신사(GTM): getGtmAttr className/data-* 를 root 에 spread → GTM viewport 자동 발화.
  // 29CM(Snowplow): useTrackImpression(IntersectionObserver) 으로 track().
  const { className: gtmImpressionClass, ...gtmImpressionAttrs } =
    buildSeedingCampaignImpressionGtmAttr({ campaignId, index, brandId, curatorId });
  const { ref: impressionRef } = useTrackImpression<HTMLAnchorElement>({
    onImpression: () => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.IMPRESSION_CONTENT,
          data: {
            page_name: 'curator_seeding_campaign_list',
            section_name: 'CuratorCampaignList',
            section_title: 'CuratorCampaignList',
            on_site_content_id: campaignId,
            position: index + 1,
          },
        });
      }
    },
  });

  // 시딩 캠페인 리스트 > 캠페인 클릭 (GTM + Amplitude 무신사 dual-fire, amp-seeding-list.md no.5)
  const handleClick = () => {
    if (isMusinsaService(service)) {
      trackSeedingCampaignClickMusinsa({ campaignId, index, brandId, curatorId });
      trackSeedingCampaignClickAmplitude({ campaignId, brandId, curatorId });
    } else if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_CONTENT,
        data: {
          page_name: 'curator_seeding_campaign_list',
          section_name: 'CuratorCampaignList',
          section_title: 'CuratorCampaignList',
          on_site_content_id: campaignId,
          position: index + 1,
        },
      });
    }
  };

  return (
    <Link
      ref={impressionRef}
      href={paths.campaignSeedingRecruitmentDetail({ campaignId })}
      onClick={handleClick}
      // grid 셀 stretch 에 맞춰 카드도 세로로 꽉 채워 짧은 item 하단 여백 제거
      className={`flex flex-col h-full${className ? ` ${className}` : ''}${gtmImpressionClass ? ` ${gtmImpressionClass}` : ''}`}
      {...gtmImpressionAttrs}
    >
      <div
        className={`relative w-full ${is29cm ? 'aspect-square' : 'aspect-[125/150]'} overflow-hidden bg-gray-100`}
      >
        {/** biome-ignore lint/performance/noImgElement: simple placeholder image */}
        <img src={goodsImage} alt={goodsName} className="w-full h-full object-cover" />
        <div
          className="absolute z-10 flex items-center"
          style={{
            bottom: 8,
            left: 8,
            padding: '1px 4px',
            height: 16, // 남은시간 라벨 height 16px. (SID-4456)
            borderRadius: 2,
            backgroundColor: '#4A4A4A',
          }}
        >
          <Typography variant="textEtc11pxReg" colorScheme="white">
            {ddayLabel}
          </Typography>
        </div>
      </div>
      <div className="pt-[12px]! pr-[8px]! pb-[24px]! pl-[12px]! flex flex-col grow bg-white">
        {/* brandName 이 빈 문자열이어도 한 줄 높이를 유지하도록 nbsp fallback */}
        <Typography variant="textEtc11pxSemibold" lineClamp="one" className="mb-[4px]!">
          {brandName || ' '}
        </Typography>
        <Typography variant="body12pxReg" lineClamp="two" className="mb-[4px]!">
          {displayGoodsName}
        </Typography>
        <Typography variant="body13pxSemi" className="mb-[2px]!">
          {goodsPrice.toLocaleString()}원
        </Typography>
        {snsChannels.length > 0 && (
          <div className="flex gap-[2px]">
            {snsChannels.map((channel) => (
              <SnsChannelIcon key={channel} channel={channel} />
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};
